// The curated DrumGenius loop set (built by scripts/build-drums.mjs into
// public/drums/). The manifest carries each loop's native tempo and exact
// beat count so the player can rate-match and re-anchor it to the beat grid.

export interface DrumLoop {
  key: string;
  label: string;
  bpm: number;
  beatsPerBar: number;
  category: string;
  file: string;
  seconds: number;
  beats: number;
  /**
   * Absent (the normal case) means the loop's tempo divides its file into whole
   * beats, so it sits exactly on the grid. `false` means neither the embedded
   * marker nor the DrumGenius listing did — the loop still plays, but it will
   * drift against the beat, so the picker says so rather than pretending.
   */
  tempoVerified?: boolean;
  /** Where an uploaded loop plays from (a signed URL, #430); bundled loops use `file`. */
  url?: string;
  /**
   * Where the loop's ride skip note sits, a fraction of the beat, measured from
   * its audio (audio/loopSwing.ts, written by scripts/measure-drum-swing.ts):
   * null when the loop shows no clear one (straight, a ride on quarters, a
   * sixteenth groove), absent when never measured (an uploaded loop, measured
   * by the player as it decodes). A band swinging with the kit lands its "and"s
   * here (docs/swing-plan.md §4.1).
   */
  swing?: number | null;
}

let cached: Promise<DrumLoop[]> | null = null;

/** Fetch (once) the drum-loop manifest; resolves to [] when unavailable. */
export function loadDrumManifest(): Promise<DrumLoop[]> {
  cached ??= fetch(`${import.meta.env.BASE_URL}drums/manifest.json`)
    .then((res) => (res.ok ? (res.json() as Promise<DrumLoop[]>) : []))
    .catch(() => []);
  return cached;
}

/** URL of a loop's audio file under the deploy base. */
export function drumLoopURL(loop: DrumLoop): string {
  return loop.url ?? `${import.meta.env.BASE_URL}drums/${loop.file}`;
}

/**
 * The loops that fit a tune's bar. A loop whose bar is a whole *multiple* of
 * the tune's still lands right — two 2/4 bars are one 4/4 bar, so the loop's
 * downbeat always coincides with a tune downbeat — which is what lets a 2/4
 * tune play the 4/4 kits instead of getting no drums at all.
 *
 * The converse is deliberately excluded: a loop shorter than the bar would
 * repeat *inside* it and re-accent the halfway point as a downbeat, which
 * changes the feel rather than merely spanning it.
 *
 * Exact-meter loops sort first, so a native match always wins the default pick
 * and existing 4/4 and 3/4 charts behave exactly as before.
 */
export function loopsForMeter(loops: DrumLoop[], beatsPerBar: number): DrumLoop[] {
  if (!Number.isFinite(beatsPerBar) || beatsPerBar <= 0) return [];
  const fits = loops.filter((l) => l.beatsPerBar > 0 && l.beatsPerBar % beatsPerBar === 0);
  return [
    ...fits.filter((l) => l.beatsPerBar === beatsPerBar),
    ...fits.filter((l) => l.beatsPerBar !== beatsPerBar),
  ];
}
